import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useEffect } from "react";
import { setCustomVariables } from "@/lib/customVariablesRegistry";

export interface CustomVariable {
  id: string;
  user_id: string;
  key: string;
  label: string;
  default_value: string | null;
  category: string;
  created_at: string;
  updated_at: string;
}

export interface CustomVariableInput {
  key: string;
  label: string;
  default_value?: string | null;
  category?: string | null;
}

/** Fetch + manage the current user's reusable template variables. */
export function useCustomVariables() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["custom-variables"],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) return [] as CustomVariable[];
      const { data, error } = await supabase
        .from("custom_variables")
        .select("*")
        .order("label", { ascending: true });
      if (error) throw error;
      return (data || []) as CustomVariable[];
    },
    staleTime: 30_000,
  });

  // Push the loaded list into the runtime registry so the highlighter and
  // variable picker know about user-defined keys without prop drilling.
  useEffect(() => {
    setCustomVariables(query.data || []);
  }, [query.data]);

  const create = useMutation({
    mutationFn: async (input: CustomVariableInput) => {
      const { data: session } = await supabase.auth.getSession();
      const userId = session.session?.user?.id;
      if (!userId) throw new Error("Not signed in");
      const { data, error } = await supabase
        .from("custom_variables")
        .insert({
          user_id: userId,
          key: input.key,
          label: input.label,
          default_value: input.default_value || null,
          category: (input.category || "general").toLowerCase(),
        })
        .select()
        .single();
      if (error) throw error;
      return data as CustomVariable;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-variables"] });
      toast.success("Variable created");
    },
    onError: (e: any) => toast.error(e?.message?.includes("duplicate") ? "A variable with that key already exists" : (e?.message || "Failed to create variable")),
  });

  const update = useMutation({
    mutationFn: async ({ id, ...input }: CustomVariableInput & { id: string }) => {
      const { data, error } = await supabase
        .from("custom_variables")
        .update({
          key: input.key,
          label: input.label,
          default_value: input.default_value || null,
          category: (input.category || "general").toLowerCase(),
        })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as CustomVariable;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-variables"] });
      toast.success("Variable updated");
    },
    onError: (e: any) => toast.error(e?.message || "Failed to update variable"),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("custom_variables").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-variables"] });
      toast.success("Variable deleted");
    },
    onError: (e: any) => toast.error(e?.message || "Failed to delete variable"),
  });

  return {
    variables: query.data || [],
    isLoading: query.isLoading,
    create: create.mutateAsync,
    update: update.mutateAsync,
    remove: remove.mutateAsync,
    isMutating: create.isPending || update.isPending || remove.isPending,
  };
}